import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import { tierOptions } from '@/lib/query';
import type { HrLeaveBalanceView, HrLeaveType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

function formatDays(value: number | undefined) {
  if (value == null || !Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

function formatRemaining(value: number | undefined) {
  if (value == null || !Number.isFinite(value)) return null;
  const n = formatDays(value);
  return `${n} day${Number(n) === 1 ? '' : 's'} remaining`;
}

export function LeaveTypeSelect({
  label = 'Leave type',
  value = '',
  onChange,
  placeholder,
  className,
  required,
  employeeId,
  essScope = false,
  balances: balancesProp,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  employeeId?: number;
  essScope?: boolean;
  balances?: HrLeaveBalanceView[];
}) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('hr');
  const [types, setTypes] = useState<HrLeaveType[]>([]);
  const [loading, setLoading] = useState(true);

  const hasEmployeeContext = essScope || (employeeId != null && employeeId > 0);

  const balancesQuery = useQuery({
    queryKey: essScope
      ? (['hr', 'ess', 'leave-balance-summary'] as const)
      : (['hr', 'leave-balance-summary', employeeId] as const),
    queryFn: () =>
      essScope ? hrApi.essLeaveBalanceSummary() : hrApi.leaveBalanceSummary(employeeId!),
    enabled: hasEmployeeContext && !balancesProp,
    ...tierOptions('operational'),
  });

  const balances = balancesProp ?? balancesQuery.data ?? [];
  const balancesLoading = hasEmployeeContext && !balancesProp && balancesQuery.isLoading;

  const balanceByTypeId = useMemo(() => {
    const map = new Map<number, HrLeaveBalanceView>();
    for (const row of balances) {
      map.set(row.leaveTypeId, row);
    }
    return map;
  }, [balances]);

  useEffect(() => {
    hrApi
      .listLeaveTypes()
      .then(setTypes)
      .catch((err) => showApiError(err, t('errors.failedToLoadLeaveTypes')))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      types
        .filter((type) => type.active !== false)
        .map((type) => {
          const balance = balanceByTypeId.get(type.id);
          let suffix = '';
          if (hasEmployeeContext && !balancesLoading) {
            if (balance?.unlimitedLeave) {
              suffix = ' — unlimited';
            } else if (balance?.policyRequired) {
              suffix = ' — policy required';
            } else {
              const remaining = balance ? formatRemaining(balance.availableDays) : null;
              suffix = remaining ? ` — ${remaining}` : ' — no balance';
            }
          }
          return {
            value: String(type.id),
            label: `${type.name} (${type.code})${suffix}`,
          };
        }),
    [types, balanceByTypeId, hasEmployeeContext, balancesLoading],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectLeaveType}
      loading={loading}
      searchPlaceholder={selectLabels.searchLeaveTypes}
      emptyMessage={selectLabels.noLeaveTypes}
    />
  );
}
